import { useState } from 'react'
import { motion } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  Building2,
  FileText,
  TrendingUp,
  Users,
  Plus,
  ArrowRight,
  BarChart3,
  Rocket,
  ShieldAlert,
  Mail,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { api } from '@/lib/api-client'
import { Organization } from '@/features/organizations/types'
import { Assessment } from '@/features/assessments/types'
import { extractItems } from '@/lib/response-utils'
import { CreateAssessmentForm } from '@/features/assessments/forms/create-assessment-form'
import { AuthorizationGate } from '@/features/authorization/AuthorizationGate'
import { useAuthorization } from '@/features/authorization/AuthorizationContext'
import { OrganizationCard } from '@/features/organizations/components/organization-card'
import { useAuthentication } from '@/features/auth/AuthenticationContext'



// ── Main Component ──
export function DashboardPage() {
  const [startAssessmentOrgId, setStartAssessmentOrgId] = useState<string | null>(null)
  const { actions: userActions, isLoading: authzLoading } = useAuthorization()
  const { user } = useAuthentication()
  const isAdmin = user?.is_administrator === true

  const { data: organizations, isLoading: orgsLoading } = useQuery({
    queryKey: ['organizations'],
    queryFn: () => api.organizations.list().then(res => extractItems<Organization>(res)),
  })

  const { data: assessments, isLoading: assessmentsLoading } = useQuery({
    queryKey: ['assessments'],
    queryFn: () => api.assessments.list().then(res => extractItems<Assessment>(res)),
  })

  const { data: maturityOverview } = useQuery({
    queryKey: ['stats', 'maturity-overview'],
    queryFn: () => api.stats.getMaturityOverview().then(res => res.data),
  })

  const isLoading = orgsLoading || assessmentsLoading

  const canView = (action: string) => isAdmin || userActions.has(action)

  const allStats = [
    {
      name: 'Organizations',
      value: organizations?.length || 0,
      icon: Building2,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      action: 'view_organization',
    },
    {
      name: 'Assessments',
      value: assessments?.length || 0,
      icon: FileText,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      action: 'view_assessment',
    },
    {
      name: 'Completed',
      value: assessments?.filter((a: Assessment) => a.status === 'completed').length || 0,
      icon: TrendingUp,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
      action: 'view_assessment',
    },
    {
      name: 'In Progress',
      value: assessments?.filter((a: Assessment) => a.status === 'in_progress').length || 0,
      icon: Users,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
      action: 'view_assessment',
    },
  ]

  // Filter stats by user's actions
  const stats = allStats.filter(stat => canView(stat.action))

  const hasOrgs = organizations && organizations.length > 0
  const hasNoActions = !authzLoading && !isAdmin && userActions.size === 0


  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <motion.div
      className="space-y-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
            Dashboard
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Welcome to your IT Maturity Assessment platform
          </p>
        </div>
      </div>

      {/* No Permissions — Contact Admin */}
      {hasNoActions && (
        <Card className="border-dashed border-2 border-amber-200 bg-amber-50/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-600" />
              No Permissions Assigned
            </CardTitle>
            <CardDescription>
              Your account doesn't have any permissions yet. Contact your administrator to get started.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="flex items-start gap-3 p-3 bg-white rounded-lg shadow-sm">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-100 text-amber-700 font-bold text-sm shrink-0">
                  1
                </div>
                <div>
                  <p className="font-medium text-sm text-gray-900">Contact Your Admin</p>
                  <p className="text-xs text-gray-500 mt-0.5">Ask them to assign you a role</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-white rounded-lg shadow-sm opacity-60">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-gray-500 font-bold text-sm shrink-0">
                  2
                </div>
                <div>
                  <p className="font-medium text-sm text-gray-900">Get a Role Assigned</p>
                  <p className="text-xs text-gray-500 mt-0.5">Your admin will configure your permissions</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-white rounded-lg shadow-sm opacity-60">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-gray-500 font-bold text-sm shrink-0">
                  3
                </div>
                <div>
                  <p className="font-medium text-sm text-gray-900">Start Working</p>
                  <p className="text-xs text-gray-500 mt-0.5">Access features based on your role</p>
                </div>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2 text-sm text-amber-700 bg-amber-100 p-3 rounded-lg">
              <Mail className="h-4 w-4 shrink-0" />
              <span>Reach out to your system administrator or team lead to request appropriate access permissions.</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Getting Started Wizard — shown when no organizations exist */}
      <AuthorizationGate actions={['view_organization']}>
        {!hasOrgs && (
          <Card className="border-dashed border-2 border-blue-200 bg-blue-50/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Rocket className="h-5 w-5 text-blue-600" />
                Getting Started
              </CardTitle>
              <CardDescription>
                Follow these steps to start your first maturity assessment
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="flex items-start gap-3 p-3 bg-white rounded-lg shadow-sm">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-bold text-sm shrink-0">
                    1
                  </div>
                  <div>
                    <p className="font-medium text-sm text-gray-900">Create an Organization</p>
                    <p className="text-xs text-gray-500 mt-0.5">Define who you're assessing</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-white rounded-lg shadow-sm opacity-60">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-gray-500 font-bold text-sm shrink-0">
                    2
                  </div>
                  <div>
                    <p className="font-medium text-sm text-gray-900">Start an Assessment</p>
                    <p className="text-xs text-gray-500 mt-0.5">Answer maturity questions</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-white rounded-lg shadow-sm opacity-60">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-gray-500 font-bold text-sm shrink-0">
                    3
                  </div>
                  <div>
                    <p className="font-medium text-sm text-gray-900">View Results</p>
                    <p className="text-xs text-gray-500 mt-0.5">Get maturity scores & recommendations</p>
                  </div>
                </div>
              </div>
              <AuthorizationGate actions={['create_organization']}>
                <div className="mt-4">
                  <Button asChild>
                    <Link to="/organizations">
                      <Plus className="mr-2 h-4 w-4" />
                      Create Your First Organization
                    </Link>
                  </Button>
                </div>
              </AuthorizationGate>
            </CardContent>
          </Card>
        )}
      </AuthorizationGate>

      {/* Stats */}
      {stats.length > 0 && (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat, i) => (
            <motion.div
              key={stat.name}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: i * 0.08 }}
              className="bg-white overflow-hidden shadow rounded-lg"
            >
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className={`p-3 rounded-md ${stat.bgColor}`}>
                      <stat.icon className={`h-6 w-6 ${stat.color}`} />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        {stat.name}
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {stat.value}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Maturity Overview */}
      <AuthorizationGate actions={['view_organization']}>
        {maturityOverview?.organizations && maturityOverview.organizations.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Maturity Overview
                </h3>
                <p className="text-sm text-gray-500 mt-0.5">Current maturity level per organization</p>
              </div>
              <Button variant="outline" asChild>
                <Link to="/organizations">
                  View All
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {maturityOverview.organizations.map((org: any, i: number) => (
                <OrganizationCard
                  key={org.id}
                  org={org}
                  index={i}
                  onStartAssessment={setStartAssessmentOrgId}
                />
              ))}
            </div>

            {/* Legend */}
            {maturityOverview.organizations.some((o: any) => o.industry_standard != null) && (
              <div className="flex items-center gap-3 mt-3 text-xs text-gray-500">
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 bg-amber-500 rounded-sm rotate-45" />
                  <span>Industry Standard</span>
                </div>
              </div>
            )}
          </div>
        )}
      </AuthorizationGate>


      {/* Start Assessment Form */}
      {startAssessmentOrgId && (
        <CreateAssessmentForm
          organizationId={startAssessmentOrgId}
          autoStart
          onClose={() => setStartAssessmentOrgId(null)}
          onSuccess={() => setStartAssessmentOrgId(null)}
        />
      )}
    </motion.div>
  )
}
